import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const StudentResult = () => {
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [course, setCourse] = useState('');
  const [grade, setGrade] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchResults = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/results`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          search: searchTerm,
          course,
          grade,
          page: currentPage,
          limit: 10
        }
      });

      const data = await response.json();
      if (data.status) {
        setResults(data.data);
        setTotalPages(data.totalPages);
        setLoading(false);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to fetch results');
    }
  };

  useEffect(() => {
    fetchResults();
  }, [searchTerm, course, grade, currentPage]);

  if (loading) {
    return (
      <div className="p-6 bg-gradient-to-br from-white to-blue-50 min-h-screen">
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const handleDelete = async (resultId) => {
    if (!window.confirm('Are you sure you want to delete this result?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/results/${resultId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.status) {
        toast.success('Result deleted successfully');
        // Refresh results
        setCurrentPage(1);
        fetchResults();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error deleting result:', error);
      toast.error('Failed to delete result');
    }
  };

  const handleEdit = (resultId) => {
    navigate(`/admin-panel/edit-result/${resultId}`);
  };

  const handleView = (resultId, result) => {
    navigate(`/exam-result/${resultId || 'unknown'}`, {
      state: {
        stats: {
          correct: result.details?.correct,
          mistake: result.details?.mistake,
          missing: result.details?.missing,
          totalMistake: result.details?.totalMistake || result.mistakes,
          accuracy: result.accuracy,
          dictationSpeed: result.wordsPerMinute,
          totalWords: result.details?.refWords?.length || result.details?.totalWords,
          typedWords: result.details?.userWords?.length || result.details?.typedWords,
          time: result.timeTaken,
          date: new Date(result.date || result.completedAt).toLocaleDateString(),
          punctuation: result.punctuation || result.details?.punctuation || 0,
          userWords: result.details?.userWords,
          refWords: result.details?.refWords,
          comparison: result.details?.comparison || 0,
          detailedPenalties: result.details?.detailedPenalties || [],
          selectedRule: result.selectedRule || { name: 'Standard' },
        },
        userInput: result.typedText,
        reference: result.referenceText,
        audioUrl: result.audioUrl,
        topicName: result.test?.title || 'Untitled Test',
      },
    });
  };

  return (
    <div className="p-6 bg-gradient-to-br from-white to-blue-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Student Results</h1>
        <Link
          to="/admin-panel/add-result"
          className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-sm"
        >
          Add New Result
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <input
            type="text"
            placeholder="Search results..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-3 bg-blue-50 text-gray-700 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400"
          />
          <select
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            className="px-4 py-3 bg-blue-50 text-gray-700 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Courses</option>
            <option value="typing">Typing</option>
            <option value="shorthand">Shorthand</option>
            <option value="theory">Theory</option>
          </select>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="px-4 py-3 bg-blue-50 text-gray-700 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Grades</option>
            <option value="A">Grade A</option>
            <option value="B">Grade B</option>
            <option value="C">Grade C</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-blue-100 text-blue-900 uppercase text-sm">
            <tr>
              <th className="px-6 py-4">Student Name</th>
              <th className="px-6 py-4">Course</th>
              <th className="px-6 py-4">Exam Date</th>
              <th className="px-6 py-4">Grade</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {results.map((result) => (
              <tr key={result._id} className="hover:bg-blue-50 transition-colors">
                <td className="px-6 py-4 font-medium">{result.user.name}</td>
                <td className="px-6 py-4">{result?.test?.title}</td>
                <td className="px-6 py-4">
                  {new Date(result.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${result.gradeClass}`}>
                    {result.grade}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    {/* <button
                      onClick={() => handleEdit(result._id)}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button> */}
                    <button
                      onClick={() => handleView(result._id,result)}
                      className="text-blue-600 hover:underline"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(result._id)}
                      className="text-red-500 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="text-gray-600">
          Showing {results.length} of {results.length} entries
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-4 py-2 rounded-lg transition ${page === currentPage
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-blue-600 border border-blue-500 hover:bg-blue-100'
                }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentResult;
